import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Flame, Bookmark, Share2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReactionPanel — appears under a publicly-shared card after the
 * recipient opens it.
 *
 * iter-94 — micro-emotional feedback overhaul:
 *   • Tap on FELT spawns 6 floating hearts that drift up and fade.
 *   • Tap on HELD spawns 4 candle sparks that flicker upward.
 *   • Tap on KEEP snaps the bookmark with a tilt + scale impulse.
 *   • Tap on SHARE pulses outward with a soft ripple.
 *   • Tap on REMIX spins the icon 360°.
 *   • Active state on each pill renders a glowing colored outline ring
 *     in its accent hue (felt → rose, held → amber, keep → mint,
 *     share → sky, remix → amber).
 *   • A 1.8s ephemeral acknowledgement caption appears below the dock
 *     ("felt · counted" / "saved to your moodboard" etc.) so the user
 *     feels the action confirmed without staring at a toast.
 */
function getViewerId() {
    try {
        let v = window.localStorage.getItem("moodcard.viewer_id");
        if (!v) {
            v = `vis_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
            window.localStorage.setItem("moodcard.viewer_id", v);
        }
        return v;
    } catch {
        return `vis_${Math.random().toString(36).slice(2)}`;
    }
}

// iter-94/98 — particle field rendered above a button when an action lands.
// Each particle is a small icon/character that floats up toward the top of
// the card and fades out. iter-98 widens the rise distance and adds a mix
// of LARGE and small particles for emotional weight.
function ParticleBurst({ trigger, kind, accent }) {
    const PARTICLE_COUNTS = { felt: 9, candle: 6, keep: 1, share: 8, remix: 0 };
    const count = PARTICLE_COUNTS[kind] || 0;
    if (!count || !trigger) return null;
    const Glyph = kind === "felt" ? Heart : kind === "candle" ? Flame : kind === "keep" ? Bookmark : Share2;
    // Compute travel distance from the dock to (roughly) the top of the
    // viewport. The dock sits ~64px from the bottom; the card occupies
    // the rest. We use 75vh as the ceiling so hearts read as "ascending
    // through the card" not just "popping above the dock".
    let viewportHeight = 800;
    try {
        viewportHeight =
            (typeof window !== "undefined" && (window.visualViewport?.height || window.innerHeight)) || 800;
    } catch { /* ignore */ }
    const maxLift = Math.max(360, viewportHeight * 0.72);
    const particles = Array.from({ length: count }).map((_, i) => {
        // Spread particles across a 100px horizontal range, each with
        // randomized lift, drift, rotation, delay, and scale. Lift
        // jitters per particle so some go higher than others — natural.
        const offsetX = (i - (count - 1) / 2) * 14 + (Math.random() * 12 - 6);
        const liftJitter = 0.65 + Math.random() * 0.45; // 0.65 .. 1.10
        const lift = maxLift * liftJitter;
        const drift = (Math.random() * 48 - 24);
        const rot = (Math.random() * 90 - 45);
        // iter-98 — heavy size variance for emotional weight.
        // ~30% of particles render as LARGE (1.4-1.8x), the rest are
        // small (0.5-1.0x). Creates a "rain of differently weighted
        // hearts" effect rather than a uniform cloud.
        const isBig = Math.random() < 0.32;
        const scale = isBig ? 1.4 + Math.random() * 0.4 : 0.5 + Math.random() * 0.5;
        const duration = 1.4 + Math.random() * 0.6; // 1.4-2.0s
        const delay = i * 0.05 + Math.random() * 0.08;
        return { i, offsetX, lift, drift, rot, scale, delay, duration, isBig };
    });
    return (
        <div className="pointer-events-none fixed left-0 right-0 top-0 z-30" aria-hidden="true">
            {particles.map((p) => (
                <motion.span
                    key={`${trigger}-${p.i}`}
                    initial={{ opacity: 0, scale: 0.2, rotate: 0 }}
                    animate={{
                        opacity: [0, 1, 1, 0],
                        // Negative y in framer means moving up. We measure
                        // from the dock origin via `transform-origin: bottom`.
                        y: [0, -p.lift * 0.55, -p.lift],
                        x: [p.offsetX, p.offsetX + p.drift * 0.55, p.offsetX + p.drift],
                        scale: [0.2, p.scale * 1.05, p.scale * 0.85],
                        rotate: [0, p.rot * 0.6, p.rot],
                    }}
                    transition={{ duration: p.duration, delay: p.delay, ease: [0.32, 0.62, 0.36, 1] }}
                    className="absolute"
                    style={{
                        // Anchor the burst origin at the bottom of the
                        // viewport (above the dock). Hearts ascend
                        // through the card from here.
                        bottom: "92px",
                        left: "50%",
                        color: accent,
                        // Heavier outer-glow on the big particles for
                        // that "this one really landed" feel.
                        filter: p.isBig
                            ? `drop-shadow(0 6px 18px ${accent}99) drop-shadow(0 0 8px ${accent})`
                            : `drop-shadow(0 3px 9px ${accent}66)`,
                    }}
                >
                    <Glyph
                        // Big particles render larger raw icon size so the
                        // scale isn't fighting the SVG resolution.
                        className={p.isBig ? "w-6 h-6" : "w-3.5 h-3.5"}
                        strokeWidth={1.6}
                        fill={kind === "felt" ? accent : "none"}
                    />
                </motion.span>
            ))}
        </div>
    );
}

function ReactionButton({ kind, active, count, onTap, Icon, label, accent, pulse, burstTrigger, spinTrigger }) {
    return (
        <motion.button
            type="button"
            onClick={onTap}
            data-testid={`reaction-${kind}-button`}
            aria-pressed={active}
            aria-label={label}
            className={[
                "btn-press relative flex flex-col items-center justify-center gap-1 px-2.5 py-2.5 rounded-2xl border transition-colors min-w-[58px] flex-1",
                active
                    ? "bg-white/[0.08]"
                    : "bg-white/[0.04] hover:bg-white/[0.08] border-white/8",
            ].join(" ")}
            style={
                active
                    ? {
                        // iter-94 — colored ring glow on active state.
                        borderColor: accent,
                        boxShadow: `0 0 0 1px ${accent}66, 0 0 16px ${accent}33`,
                    }
                    : undefined
            }
            whileTap={{ scale: 0.92 }}
        >
            {/* Particle burst — rendered above the icon when triggered */}
            <ParticleBurst trigger={burstTrigger} kind={kind} accent={accent} />

            <motion.div
                animate={
                    spinTrigger
                        ? { rotate: [0, -360], scale: [1, 1.15, 1] }
                        : pulse && !active
                            ? { scale: [1, 1.12, 1] }
                            : { scale: 1 }
                }
                transition={
                    spinTrigger
                        ? { duration: 0.55, ease: [0.32, 0.72, 0.36, 1] }
                        : pulse && !active
                            ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" }
                            : { duration: 0.2 }
                }
                style={{ color: active ? accent : "rgba(245, 240, 230, 0.72)" }}
            >
                <Icon
                    className="w-5 h-5"
                    strokeWidth={1.6}
                    fill={active ? accent : "none"}
                />
            </motion.div>
            <div className="flex items-baseline gap-1">
                <span
                    className="font-sans text-[9px] uppercase tracking-[0.28em]"
                    style={{ color: active ? accent : "rgba(245, 240, 230, 0.62)" }}
                >
                    {label}
                </span>
                {count > 0 && (
                    <span
                        className="font-sans text-[10px] font-medium tabular-nums"
                        style={{ color: active ? accent : "rgba(245, 240, 230, 0.72)" }}
                    >
                        {count}
                    </span>
                )}
            </div>
        </motion.button>
    );
}

// iter-94 — ephemeral caption that fades in/out after an action lands.
function AckCaption({ message, accent }) {
    return (
        <AnimatePresence>
            {message && (
                <motion.div
                    key={message}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.35 }}
                    className="mt-2 font-sans text-[10px] uppercase tracking-[0.32em] text-center"
                    style={{ color: accent }}
                    data-testid="reaction-ack-caption"
                >
                    {message}
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function ReactionPanel({ slug, initialCounts = {}, onShare, onRemix, dock = false }) {
    const [counts, setCounts] = useState({
        felt: initialCounts.felt || 0,
        candle: initialCounts.candle || 0,
    });
    const [pending, setPending] = useState(null);
    const [feltActive, setFeltActive] = useState(false);
    const [candleActive, setCandleActive] = useState(false);
    const [kept, setKept] = useState(false);

    // iter-94 — burst triggers (incrementing counter per kind so the
    // particle component re-mounts and re-animates on every tap).
    const [burst, setBurst] = useState({ felt: 0, candle: 0, keep: 0, share: 0 });
    const [spin, setSpin] = useState({ remix: 0, keep: 0 });
    const [ackMsg, setAckMsg] = useState(null);
    const [ackAccent, setAckAccent] = useState("#e8d5a8");
    const ackTimer = useRef(null);

    useEffect(() => {
        try {
            const f = window.localStorage.getItem(`moodcard.felt.${slug}`) === "1";
            const c = window.localStorage.getItem(`moodcard.candle.${slug}`) === "1";
            const k = window.localStorage.getItem(`moodcard.kept.${slug}`) === "1";
            setFeltActive(f); setCandleActive(c); setKept(k);
        } catch { /* ignore */ }
    }, [slug]);

    useEffect(() => () => { if (ackTimer.current) clearTimeout(ackTimer.current); }, []);

    const showAck = (msg, color) => {
        setAckMsg(msg);
        setAckAccent(color || "#e8d5a8");
        if (ackTimer.current) clearTimeout(ackTimer.current);
        ackTimer.current = setTimeout(() => setAckMsg(null), 1800);
    };

    const react = async (kind) => {
        if (pending) return;
        setPending(kind);
        const wasActive = kind === "felt" ? feltActive : candleActive;
        const willActivate = !wasActive;
        if (kind === "felt") setFeltActive(willActivate);
        else setCandleActive(willActivate);
        setCounts((c) => ({
            ...c,
            [kind]: Math.max(0, (c[kind] || 0) + (willActivate ? 1 : -1)),
        }));

        // iter-94 — fire animation + ack ONLY when activating.
        if (willActivate) {
            setBurst((b) => ({ ...b, [kind]: b[kind] + 1 }));
            if (kind === "felt") showAck("felt · counted", "rgb(244, 114, 182)");
            else showAck("held · they'll feel this", "rgb(251, 191, 36)");
        }

        try {
            const { data } = await axios.post(`${API}/public/c/${slug}/react`, {
                viewer_id: getViewerId(),
                kind,
            });
            if (data?.counts) setCounts(data.counts);
            try {
                window.localStorage.setItem(`moodcard.${kind}.${slug}`, data?.active ? "1" : "0");
            } catch { /* ignore */ }
        } catch {
            if (kind === "felt") setFeltActive(wasActive);
            else setCandleActive(wasActive);
            setCounts((c) => ({
                ...c,
                [kind]: Math.max(0, (c[kind] || 0) + (willActivate ? -1 : 1)),
            }));
        } finally {
            setPending(null);
        }
    };

    const toggleKeep = () => {
        const next = !kept;
        setKept(next);
        // iter-94 — bookmark snap + spark + ack only when saving (not on unsave).
        if (next) {
            setSpin((s) => ({ ...s, keep: s.keep + 1 }));
            setBurst((b) => ({ ...b, keep: b.keep + 1 }));
            showAck("saved to your moodboard", "rgb(167, 243, 208)");
        }
        try {
            window.localStorage.setItem(`moodcard.kept.${slug}`, next ? "1" : "0");
        } catch { /* ignore */ }
    };

    const triggerShare = () => {
        setBurst((b) => ({ ...b, share: b.share + 1 }));
        showAck("sharing…", "rgb(186, 230, 253)");
        if (onShare) onShare();
    };

    const triggerRemix = () => {
        setSpin((s) => ({ ...s, remix: s.remix + 1 }));
        showAck("re-rendering…", "rgb(251, 191, 36)");
        if (onRemix) onRemix();
    };

    // Light haptic on tap.
    const haptic = () => {
        try { if (navigator?.vibrate) navigator.vibrate(8); } catch { /* ignore */ }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className={
                dock
                    ? "fixed bottom-3 left-3 right-3 z-40 flex flex-col items-center pointer-events-none"
                    : "mt-5 flex flex-col items-center"
            }
            data-testid="reaction-panel"
            style={dock ? { paddingBottom: "max(0px, env(safe-area-inset-bottom))" } : undefined}
        >
            <div className={dock ? "pointer-events-auto w-full max-w-md" : ""}>
                {!feltActive && !candleActive && !kept && !dock && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2, duration: 0.7 }}
                        className="font-sans text-[10px] uppercase tracking-[0.42em] text-app-3 mb-3 text-center"
                        data-testid="reaction-panel-hint"
                    >
                        touch the heart if it landed
                    </motion.p>
                )}
                <div
                    className={
                        dock
                            ? "flex items-stretch gap-1.5 px-2 py-2 rounded-full bg-black/55 border border-white/10 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
                            : "flex items-stretch gap-1.5"
                    }
                    onClickCapture={haptic}
                >
                    <ReactionButton
                        kind="felt"
                        active={feltActive}
                        count={counts.felt}
                        onTap={() => react("felt")}
                        Icon={Heart}
                        label="felt"
                        accent="rgb(244, 114, 182)"
                        pulse={true}
                        burstTrigger={burst.felt}
                    />
                    <ReactionButton
                        kind="candle"
                        active={candleActive}
                        count={counts.candle}
                        onTap={() => react("candle")}
                        Icon={Flame}
                        label="held"
                        accent="rgb(251, 191, 36)"
                        pulse={false}
                        burstTrigger={burst.candle}
                    />
                    <ReactionButton
                        kind="keep"
                        active={kept}
                        count={0}
                        onTap={toggleKeep}
                        Icon={Bookmark}
                        label="keep"
                        accent="rgb(167, 243, 208)"
                        pulse={false}
                        burstTrigger={burst.keep}
                        spinTrigger={spin.keep}
                    />
                    {onShare && (
                        <ReactionButton
                            kind="share"
                            active={false}
                            count={0}
                            onTap={triggerShare}
                            Icon={Share2}
                            label="share"
                            accent="rgb(186, 230, 253)"
                            pulse={false}
                            burstTrigger={burst.share}
                        />
                    )}
                    {onRemix && (
                        <ReactionButton
                            kind="remix"
                            active={false}
                            count={0}
                            onTap={triggerRemix}
                            Icon={RotateCcw}
                            label="remix"
                            accent="rgb(251, 191, 36)"
                            pulse={false}
                            spinTrigger={spin.remix}
                        />
                    )}
                </div>
                <AckCaption message={ackMsg} accent={ackAccent} />
            </div>
        </motion.div>
    );
}
